import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/use-confirm";
import { MoneyInput } from "@/components/money-input";
import { BomItemPicker } from "./BomItemPicker";
import { PriceTablePickerModal } from "@/components/price-table/PriceTablePickerModal";
import { InfoHint } from "@/components/ui/info-hint";
import type { BomLineItem, PricingResult } from "@/lib/bom";
import type { BomItemSummary } from "@/lib/bom-items";
import { formatMoney } from "@/lib/format";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BomLineItemsGridProps {
  items: BomLineItem[];
  pricing: PricingResult | null;
  disabled?: boolean;
  onAddItem: (req: Record<string, unknown>) => void;
  onPatchItem: (item: BomLineItem, patch: Record<string, unknown>) => void;
  onDeleteItem: (item: BomLineItem) => void;
  onReorder: (order: { bomLineItemId: number; sortOrder: number }[]) => void;
  bomColours?: number | null;
  bomPositions?: number | null;
}

// ---------------------------------------------------------------------------
// BomLineItemsGrid
//
// Design rationale: uses a plain <table> rather than DataGrid<T>.
// DataGrid requires pagination props (pageIndex, pageSize, totalRows,
// onPageChange) that are unnecessary overhead for a small inline editable
// grid with no server-side pagination. A plain table keeps the component
// focused and testable.
// ---------------------------------------------------------------------------

export function BomLineItemsGrid({
  items,
  pricing,
  disabled = false,
  onAddItem,
  onPatchItem,
  onDeleteItem,
  onReorder,
  bomColours = null,
  bomPositions = null,
}: BomLineItemsGridProps) {
  const [picking, setPicking] = useState(false);
  const [pickerFor, setPickerFor] = useState<BomLineItem | null>(null);
  const confirm = useConfirm();

  // Deleting the LAST line item zeroes the linked quote/job line's price via
  // the II-A price write-back, so it must not happen silently. Multi-item
  // deletes stay immediate (the line still has value afterwards).
  async function handleDelete(item: BomLineItem) {
    if (items.length === 1) {
      const ok = await confirm({
        title: "Delete the last line item?",
        body: "This will set the quote line to $0. The linked quote/job line keeps no value once the BOM is empty.",
        confirmLabel: "Delete",
        intent: "destructive",
      });
      if (!ok) return;
    }
    onDeleteItem(item);
  }

  // Swap the sort orders of the item at `index` with its neighbour `delta`
  // rows away. The moved item adopts the neighbour's sortOrder and vice versa.
  function moveItem(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const a = items[index]!;
    const b = items[target]!;
    onReorder([
      { bomLineItemId: a.bomLineItemId, sortOrder: b.sortOrder },
      { bomLineItemId: b.bomLineItemId, sortOrder: a.sortOrder },
    ]);
  }

  // Build a bomLineItemId -> lineValueAud map once per pricing change so each
  // row is an O(1) lookup instead of an O(breakdown) .find() per render.
  // Entries with a null bomLineItemId can never match a real item id (the old
  // .find() never matched them either), so they are simply skipped.
  const lineValues = useMemo(() => {
    const map = new Map<number, number>();
    const col = pricing?.columns[0];
    if (col) {
      for (const entry of col.lineBreakdown) {
        if (entry.bomLineItemId != null) {
          map.set(entry.bomLineItemId, entry.lineValueAud);
        }
      }
    }
    return map;
  }, [pricing]);

  // Look up a line value for an item from the first pricing column
  const getLineValue = (bomLineItemId: number): number | null =>
    lineValues.get(bomLineItemId) ?? null;

  const handlePick = (master: BomItemSummary) => {
    onAddItem({
      bomItemId: master.bomItemId,
      itemName: master.name,
      calcType: master.calcType,
      calcFactor: master.calcFactor,
      unitCost: master.unitCost,
      unitCurrencyCode: master.unitCurrencyCode,
      priceByRange: master.priceByRange,
      unitCostByRangeJson: master.unitCostByRangeJson,
      supplierId: master.supplierId,
      itemiseOnQuote: master.itemiseOnQuote,
    });
    setPicking(false);
  };

  return (
    <div className="space-y-3">
      {/* ------------------------------------------------------------------ */}
      {/* Table                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th scope="col" className="px-3 py-2 text-left font-medium">Item</th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                <span className="inline-flex items-center gap-1">
                  Calc
                  <InfoHint label="How this component is priced — Fixed: flat charge · Quantity: cost × qty · Sheet/Per-colour/Per-position: scales with that dimension · Price table: looked-up rate." />
                </span>
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                <span className="inline-flex items-center gap-1">
                  Unit cost
                  <InfoHint label="Cost per unit. For price-table items this comes from the table." />
                </span>
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                <span className="inline-flex items-center gap-1">
                  Line value
                  <InfoHint label="This component's cost contribution at the quoted quantity." />
                </span>
              </th>
              <th scope="col" className="px-3 py-2 text-center font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  No components yet — add one
                </td>
              </tr>
            ) : (
              items.map((item, index) => {
                const lineValue = getLineValue(item.bomLineItemId);
                const valueCents = Math.round((item.unitCost ?? 0) * 100);

                return (
                  <tr
                    key={item.bomLineItemId}
                    className="border-b last:border-b-0"
                  >
                    {/* Item name */}
                    <td className="px-3 py-2">{item.itemName}</td>

                    {/* Calc type */}
                    <td className="px-3 py-2 text-muted-foreground">
                      {item.calcType}
                    </td>

                    {/* Unit cost */}
                    <td className="px-3 py-2">
                      {item.calcType === "PriceTable" ? (
                        <span className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            disabled={disabled}
                            title="Priced from a shared price table — click to choose or change it."
                            className="text-sm text-primary hover:underline"
                            onClick={() => setPickerFor(item)}
                          >
                            {item.priceTableId
                              ? `⚙ #${item.priceTableId} · ${item.rateAxis}`
                              : "Choose price table"}
                          </button>
                          {item.priceTableId ? (
                            <button
                              type="button"
                              aria-label={`Clear price table for ${item.itemName}`}
                              disabled={disabled}
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => onPatchItem(item, { clearPriceTableId: true })}
                            >
                              ✕
                            </button>
                          ) : null}
                        </span>
                      ) : (
                        <MoneyInput
                          aria-label={`Unit cost for ${item.itemName}`}
                          valueCents={valueCents}
                          onChangeCents={(cents) =>
                            onPatchItem(item, { unitCost: cents / 100 })
                          }
                          disabled={disabled}
                        />
                      )}
                    </td>

                    {/* Line value */}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {lineValue != null
                        ? formatMoney(Math.round(lineValue * 100))
                        : "—"}
                    </td>

                    {/* Reorder + Delete */}
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Move ${item.itemName} up`}
                          disabled={disabled || index === 0}
                          onClick={() => moveItem(index, -1)}
                        >
                          <ChevronUp className="size-4" aria-hidden="true" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Move ${item.itemName} down`}
                          disabled={disabled || index === items.length - 1}
                          onClick={() => moveItem(index, +1)}
                        >
                          <ChevronDown className="size-4" aria-hidden="true" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete ${item.itemName}`}
                          disabled={disabled}
                          onClick={() => void handleDelete(item)}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Add component / picker                                              */}
      {/* ------------------------------------------------------------------ */}
      {picking ? (
        <BomItemPicker
          onPick={handlePick}
          onCancel={() => setPicking(false)}
        />
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setPicking(true)}
        >
          Add component
        </Button>
      )}

      {pickerFor && (
        <PriceTablePickerModal
          open
          onOpenChange={(o) => { if (!o) setPickerFor(null); }}
          onConfirm={(v) => {
            onPatchItem(pickerFor, {
              priceTableId: v.priceTableId,
              rateAxis: v.rateAxis,
              rateColumn: v.rateColumn,
              rateTypeCode: v.rateTypeCode,
            });
            setPickerFor(null);
          }}
          bomColours={bomColours}
          bomPositions={bomPositions}
        />
      )}
    </div>
  );
}
